import { type FC, type RefObject, useState } from 'react'
import { Radio } from '@/components/commons/radio'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/translations/i18n'

interface ExperienceVettingMerchSubmitModalProps {
	isSubmitting: boolean
	type: 'ai' | 'host-edits'
	onClose: () => void
	submissionRef: RefObject<HTMLButtonElement | null>
	setRequireHostApprovalCb: (value: boolean) => void
	isOpen: boolean
}

export const ExperienceVettingMerchSubmitModal: FC<
	ExperienceVettingMerchSubmitModalProps
> = ({
	isSubmitting,
	onClose,
	submissionRef,
	type,
	setRequireHostApprovalCb,
	isOpen
}) => {
	const { t } = useTranslation()
	const [hasSelectedOption, setHasSelectedOption] = useState(false)

	const handleRadioChange = (value: boolean) => {
		setHasSelectedOption(true)
		setRequireHostApprovalCb(value)
	}

	const handleSubmit = () => {
		submissionRef.current?.click()
	}

	switch (type) {
		case 'ai':
			return (
				<DialogProvider>
					<Dialog
						hideOnInteractOutside={!isSubmitting}
						onClose={onClose}
						open={isOpen}
						style={{ width: '480px' }}
					>
						<DialogHeader className='grid grid-cols-[36px_minmax(0,1fr)_36px] items-center justify-items-center border-neutral-300 border-b'>
							<DialogDismiss disabled={isSubmitting} />
							<p className='text-neutral-900'>
								{t(
									'vetting:merch.commons.submission_modal_header'
								)}
							</p>
						</DialogHeader>
						<DialogBody className='gap-1'>
							<h3 className='font-medium text-neutral-900 text-xl'>
								{t(
									'vetting:merch.service.confirmation_ai_modal.title'
								)}
							</h3>
							<p className='text-neutral-900'>
								{t(
									'vetting:merch.service.confirmation_ai_modal.description'
								)}
							</p>
						</DialogBody>
						<DialogFooter>
							<ButtonLegacy
								disabled={isSubmitting}
								onClick={onClose}
								type='button'
								variant={'tertiary'}
							>
								{t('common:cancel')}
							</ButtonLegacy>

							<ButtonBrandLegacy
								disabled={isSubmitting}
								onClick={handleSubmit}
							>
								{t('common:confirm')}
								{isSubmitting && <Spinner size={20} />}
							</ButtonBrandLegacy>
						</DialogFooter>
					</Dialog>
				</DialogProvider>
			)

		case 'host-edits':
			return (
				<DialogProvider>
					<Dialog
						onClose={onClose}
						open={isOpen}
						style={{ width: '480px' }}
					>
						<DialogHeader className='grid grid-cols-[36px_minmax(0,1fr)_36px] items-center justify-items-center border-neutral-300 border-b'>
							<DialogDismiss />
							<p className='text-neutral-900'>
								{t(
									'vetting:merch.commons.submission_modal_header'
								)}
							</p>
						</DialogHeader>

						<DialogBody className='gap-1'>
							<div className='flex flex-col gap-4'>
								<h3 className='font-medium text-neutral-900 text-xl'>
									{t(
										'vetting:merch.service.confirmation_edits_modal.title'
									)}
								</h3>
								<Radio
									label={
										<div className='flex flex-col gap-1'>
											<p className='text-neutral-900'>
												{t(
													'vetting:merch.service.confirmation_edits_modal.radio_label_require_host_approval'
												)}
											</p>
											<small>
												{t(
													'vetting:merch.service.confirmation_edits_modal.radio_label_require_host_approval_description'
												)}
											</small>
										</div>
									}
									name='require-host-approval'
									onChange={() => handleRadioChange(true)}
								/>
								<Radio
									label={
										<div className='flex flex-col gap-1'>
											<p className='text-neutral-900'>
												{t(
													'vetting:merch.service.confirmation_edits_modal.radio_label_no_edits'
												)}
											</p>
											<small>
												{t(
													'vetting:merch.service.confirmation_edits_modal.radio_label_no_edits_description'
												)}
											</small>
										</div>
									}
									name='require-host-approval'
									onChange={() => handleRadioChange(false)}
								/>
							</div>
						</DialogBody>

						<DialogFooter>
							<ButtonLegacy
								disabled={isSubmitting}
								onClick={onClose}
								type='button'
								variant={'tertiary'}
							>
								{t('common:cancel')}
							</ButtonLegacy>

							<ButtonBrandLegacy
								disabled={isSubmitting || !hasSelectedOption}
								onClick={handleSubmit}
							>
								{t('common:confirm')}
								{isSubmitting && <Spinner size={20} />}
							</ButtonBrandLegacy>
						</DialogFooter>
					</Dialog>
				</DialogProvider>
			)
		default:
			return null
	}
}
